import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/authSlice';
import { userRoleData } from 'redux/slices/userRole';
import { landingPathForRole } from 'utils/roles';
import { stripStatePrefix } from 'utils/tenant';

/**
 * The `*` route of every route tree: shown inside the app shell for a path
 * nothing else matches.
 *
 * Legacy state-prefixed links (`/US/dashboard`) land here too, since no route
 * carries the prefix any more; they are sent on to the path without it. That
 * used to be a top-level `/:stateId/*` route, which matched every unknown path
 * and rendered nothing for the ones that were not a state, so a mistyped or
 * retired URL was a blank page.
 */
const NotFound = () => {
  const location = useLocation();
  const user = useSelector(selectUser);
  const role = useSelector(userRoleData);

  const stripped = stripStatePrefix(location.pathname || '/');
  if (stripped !== location.pathname) {
    return <Navigate to={`${stripped}${location.search || ''}`} replace state={location.state} />;
  }

  const home = landingPathForRole(user?.originalRole || user?.role || role?.userRole) || '/';

  return (
    <section
      className="mx-auto flex max-w-md flex-col items-center gap-3 px-4 py-16 text-center"
      aria-labelledby="not-found-title"
      data-testid="notFound"
    >
      <p className="text-sm font-semibold uppercase tracking-wide text-primary700">404</p>
      <h1 id="not-found-title" className="text-2xl font-bold text-primary900">
        Page not found
      </h1>
      <p className="text-sm text-gray-600">
        There is nothing at <span className="break-all font-mono">{location.pathname}</span>. The link may be
        mistyped or the page may have moved.
      </p>
      <Link
        to={home}
        className="mt-2 inline-flex items-center rounded-lg bg-primary700 px-4 py-2 text-sm font-semibold text-white hover:bg-primary900"
      >
        Go to your home page
      </Link>
    </section>
  );
};

export default NotFound;
